import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Collapse, Divider, Input, InputNumber, List, Modal, Space, Spin, Steps, Switch, Table, Tabs, Tag, Typography, message } from "antd";
import { DeleteOutlined, SendOutlined, StarFilled } from "@ant-design/icons";
import axios from "axios";
import TrainingChart from "./TrainingChart";
import LiquidGlass from "./LiquidGlass";
import RequestPremium from "./RequestPremium";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

function AiPlansBuilder({ onClose, initialCoachMessage, initialState, bootstrap, appFtp, appIsAdmin, appIsPremium, onPersistState, onAfterFinalSave, onRequestPremium }) {
  // Minimal markdown support for conversation: **bold**, *italic* (no links/html), preserve newlines.
  const renderMarkdownLiteInline = useCallback((line, keyPrefix = "") => {
    const s = String(line ?? "");
    const out = [];
    let i = 0;
    let k = 0;
    const pushText = (txt) => {
      if (!txt) return;
      out.push(<React.Fragment key={`${keyPrefix}t-${k++}`}>{txt}</React.Fragment>);
    };
    while (i < s.length) {
      // Bold: **text**
      if (s.startsWith("**", i)) {
        const j = s.indexOf("**", i + 2);
        if (j !== -1) {
          const inner = s.slice(i + 2, j);
          out.push(
            <strong key={`${keyPrefix}b-${k++}`}>
              {inner}
            </strong>
          );
          i = j + 2;
          continue;
        }
        // Unmatched ** => treat first * as literal
        pushText("*");
        i += 1;
        continue;
      }
      // Italic: *text* (but don't start on **, handled above)
      if (s[i] === "*") {
        const j = s.indexOf("*", i + 1);
        if (j !== -1) {
          const inner = s.slice(i + 1, j);
          out.push(
            <em key={`${keyPrefix}i-${k++}`}>
              {inner}
            </em>
          );
          i = j + 1;
          continue;
        }
        // Unmatched * => literal
        pushText("*");
        i += 1;
        continue;
      }

      const nextBold = s.indexOf("**", i);
      const nextIt = s.indexOf("*", i);
      let next = -1;
      if (nextBold !== -1 && nextIt !== -1) next = Math.min(nextBold, nextIt);
      else next = nextBold !== -1 ? nextBold : nextIt;
      if (next === -1) next = s.length;
      pushText(s.slice(i, next));
      i = next;
    }
    return out;
  }, []);

  const renderMarkdownLite = useCallback(
    (content) => {
      const s = String(content ?? "");
      const lines = s.split("\n");
      return lines.map((line, idx) => (
        <React.Fragment key={`ml-${idx}`}>
          {renderMarkdownLiteInline(line, `ml-${idx}-`)}
          {idx < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ));
    },
    [renderMarkdownLiteInline]
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [lastReformulation, setLastReformulation] = useState("");
  const [isEnough, setIsEnough] = useState(false);
  const convoScrollRef = useRef(null);
  const hasBootstrappedRef = useRef(false);
  const restoredFromStateRef = useRef(false);
  const [blueprint, setBlueprint] = useState(null);
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [lastBlueprintError, setLastBlueprintError] = useState("");
  const [blueprintRefineText, setBlueprintRefineText] = useState("");
  const [sessionBuilderLoading, setSessionBuilderLoading] = useState(false);
  const [sessionWorkouts, setSessionWorkouts] = useState({}); // key -> workout
  const [sessionComments, setSessionComments] = useState({}); // key -> comment string
  const [sessionNameOverrides, setSessionNameOverrides] = useState({}); // key -> string
  // Step editing drafts: keep raw user input visible even when invalid (do NOT overwrite workout until valid)
  // shape: { [sessionKey]: { [stepIdx]: { duration?: string, watt?: string, rpm?: string } } }
  const [stepDraftsBySession, setStepDraftsBySession] = useState({});
  const [userFtp, setUserFtp] = useState(null);
  // Display-only: allow switching between %FTP and absolute Watts. Storage remains %FTP (ref_ftp=100).
  const [powerDisplayMode, setPowerDisplayMode] = useState("percent"); // "percent" | "watts"
  const [displayFtp, setDisplayFtp] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [, setCreditsLimit] = useState(null);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  const premiumModalRef = useRef(null);
  const [existingCategories, setExistingCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [programCategory, setProgramCategory] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [categoryChecking, setCategoryChecking] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchFailures, setBatchFailures] = useState([]); // [{key, error}]
  const [initialBatchDone, setInitialBatchDone] = useState(false);
  const [programSaving, setProgramSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const autoBatchRanRef = useRef(false);
  const prevStepRef = useRef(null);
  const skipNextBlueprintClearRef = useRef(false);
  const step3AutoBatchEligibleRef = useRef(false);
  const step3RemoteRestoreAttemptedRef = useRef(false);
  const fetchedCategoriesRef = useRef(false);
  const didAutoSeedCategoryRef = useRef(false);

  // Admin-only: last GPT debug blobs (step-level + per-session)
  const [debugStep1, setDebugStep1] = useState(null); // {gpt_in,gpt_out}
  const [debugStep2, setDebugStep2] = useState(null); // {gpt_in,gpt_out}
  const [sessionDebug, setSessionDebug] = useState({}); // key -> {gpt_in,gpt_out}

  const capDebug = useCallback((dbg) => {
    try {
      if (!dbg || typeof dbg !== "object") return null;
      const capStr = (s, n = 60000) => {
        const v = typeof s === "string" ? s : s == null ? "" : String(s);
        if (v.length <= n) return v;
        return `${v.slice(0, n - 20)}\n... (truncated) ...`;
      };
      const next = JSON.parse(JSON.stringify(dbg));
      if (next?.gpt_in?.messages && Array.isArray(next.gpt_in.messages)) {
        next.gpt_in.messages = next.gpt_in.messages.map((m) => ({
          ...(m || {}),
          content: capStr(m?.content, 60000),
        }));
      }
      if (next?.gpt_out?.raw) next.gpt_out.raw = capStr(next.gpt_out.raw, 60000);
      if (next?.gpt_out?.error) next.gpt_out.error = capStr(next.gpt_out.error, 8000);
      return next;
    } catch {
      return dbg;
    }
  }, []);

  const DebugPanel = useCallback(
    ({ debug }) => {
      if (!isAdmin) return null;
      if (!debug || typeof debug !== "object") return null;
      const gin = debug.gpt_in ?? null;
      const gout = debug.gpt_out ?? null;
      return (
        <Collapse size="small" style={{ marginTop: 10 }}>
          <Panel header="Debug" key="debug">
            <Tabs
              items={[
                {
                  key: "in",
                  label: "GPT in",
                  children: (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                      {JSON.stringify(gin, null, 2)}
                    </pre>
                  ),
                },
                {
                  key: "out",
                  label: "GPT out",
                  children: (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                      {JSON.stringify(gout, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          </Panel>
        </Collapse>
      );
    },
    [isAdmin]
  );

  const emitPersistState = useCallback((overrides = {}) => {
    try {
      if (typeof onPersistState !== "function") return;
      const snapshot = {
        status: "in_progress",
        current_step: currentStep,
        intro_message: (initialCoachMessage || "").trim() || undefined,
        conversation: chatMessages,
        last_reformulation: lastReformulation,
        is_enough: isEnough,
        blueprint: blueprint,
        program_category: programCategory,
        session_workouts: sessionWorkouts,
        session_comments: sessionComments,
        session_name_overrides: sessionNameOverrides,
        step_drafts_by_session: stepDraftsBySession,
        // Admin-only debug (kept out for non-admins)
        debug: isAdmin
          ? {
              step1: debugStep1,
              step2: debugStep2,
              sessions: sessionDebug,
            }
          : undefined,
        ...overrides,
      };
      onPersistState(snapshot);
    } catch (e) {
      // ignore
    }
  }, [
    onPersistState,
    currentStep,
    initialCoachMessage,
    chatMessages,
    lastReformulation,
    isEnough,
    blueprint,
    programCategory,
    sessionWorkouts,
    sessionComments,
    sessionNameOverrides,
    stepDraftsBySession,
    isAdmin,
    debugStep1,
    debugStep2,
    sessionDebug,
  ]);

  const stepItems = useMemo(
    () => [
      {
        title: "Coach Intake",
        description: "Clarify goals & constraints",
      },
      {
        title: "Program Blueprint",
        description: "Week-by-week overview",
      },
      {
        title: "Session Builder",
        description: "Step-by-step workouts",
      },
    ],
    []
  );

  // Axios instance with interceptors for token handling and retries (same pattern as WorkoutParser.js)
  const axiosInstance = useMemo(() => {
    const inst = axios.create({
      baseURL: "/api",
      headers: { "Content-Type": "application/json" },
    });

    inst.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    inst.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem("refresh_token");
            const refreshResponse = await axios.post("api/refresh_tokens/", { refresh_token: refreshToken });
            if (refreshResponse.status === 200) {
              const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;
              localStorage.setItem("token", newToken);
              localStorage.setItem("refresh_token", newRefreshToken);
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return inst(originalRequest);
            }
          } catch (refreshError) {
            // Don't hard-logout here; let the calling code show a helpful error.
            console.error("Failed to refresh token:", refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return inst;
  }, []);

  // Restore state (continue flow)
  useEffect(() => {
    if (restoredFromStateRef.current) return;
    if (!initialState || typeof initialState !== "object") return;
    restoredFromStateRef.current = true;

    try {
      const step = Number(initialState.current_step);
      if (!Number.isNaN(step) && step >= 0 && step <= 2) setCurrentStep(step);
    } catch {}

    // If resuming directly in Step 3:
    // - If sessions already exist, do NOT auto-generate and do not show the blocking loader.
    // - If no sessions exist yet, auto-generate all (once category becomes valid).
    try {
      const step = Number(initialState.current_step);
      if (step === 2) {
        const restoredWorkouts = initialState.session_workouts && typeof initialState.session_workouts === "object" ? initialState.session_workouts : {};
        const hasAnyWorkout = Object.values(restoredWorkouts || {}).some(Boolean);
        autoBatchRanRef.current = hasAnyWorkout; // prevent Step3 auto-batch if we already have sessions
        step3AutoBatchEligibleRef.current = !hasAnyWorkout; // auto-batch only if empty
        setInitialBatchDone(hasAnyWorkout); // if empty, keep loader until batch completes
        skipNextBlueprintClearRef.current = true; // avoid clearing restored sessions when blueprint is set
      }
    } catch {}

    try {
      const conv = Array.isArray(initialState.conversation) ? initialState.conversation : [];
      const normalized = conv
        .filter((m) => m && typeof m === "object" && (m.role === "user" || m.role === "assistant") && typeof m.content === "string")
        .map((m) => ({ role: m.role, content: m.content }));
      if (normalized.length) setChatMessages(normalized);
    } catch {}

    try {
      if (typeof initialState.last_reformulation === "string") setLastReformulation(initialState.last_reformulation);
      if (typeof initialState.is_enough === "boolean") setIsEnough(initialState.is_enough);
    } catch {}

    try {
      if (initialState.blueprint && typeof initialState.blueprint === "object") setBlueprint(initialState.blueprint);
    } catch {}

    try {
      if (typeof initialState.program_category === "string" && initialState.program_category.trim()) {
        setProgramCategory(initialState.program_category.trim());
        didAutoSeedCategoryRef.current = true;
      }
    } catch {}

    try {
      if (initialState.session_workouts && typeof initialState.session_workouts === "object") {
        setSessionWorkouts(initialState.session_workouts);
      }
    } catch {}
    try {
      if (initialState.session_comments && typeof initialState.session_comments === "object") {
        setSessionComments(initialState.session_comments);
      }
    } catch {}
    try {
      if (initialState.session_name_overrides && typeof initialState.session_name_overrides === "object") {
        setSessionNameOverrides(initialState.session_name_overrides);
      }
    } catch {}
    try {
      if (initialState.step_drafts_by_session && typeof initialState.step_drafts_by_session === "object") {
        setStepDraftsBySession(initialState.step_drafts_by_session);
      }
    } catch {}

    // Admin-only debug restore (best-effort)
    try {
      const dbg = initialState.debug && typeof initialState.debug === "object" ? initialState.debug : null;
      if (dbg) {
        if (dbg.step1) setDebugStep1(dbg.step1);
        if (dbg.step2) setDebugStep2(dbg.step2);
        if (dbg.sessions && typeof dbg.sessions === "object") setSessionDebug(dbg.sessions);
      }
    } catch {}
  }, [initialState]);

  // Seed first coach message (provided by parent once loaded) - only if we didn't restore
  useEffect(() => {
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    if (restoredFromStateRef.current) return;
    const intro = (initialCoachMessage || "").trim();
    if (!intro) return;
    setChatMessages((prev) => (prev.length ? prev : [{ role: "assistant", content: intro }]));
  }, [initialCoachMessage]);

  // Bootstrap user/account info from parent (App.js) / intro endpoint (no /get_user_info calls here).
  useEffect(() => {
    try {
      const b = bootstrap && typeof bootstrap === "object" ? bootstrap : {};
      const ftp0 = b.user_ftp ?? b.userFtp ?? appFtp;
      const ftp = typeof ftp0 === "number" ? ftp0 : ftp0 == null ? null : Number(ftp0);
      if (typeof ftp === "number" && ftp > 10) {
        setUserFtp(ftp);
        setDisplayFtp((prev) => (typeof prev === "number" && prev > 10 ? prev : ftp));
      }
      const admin = typeof b.is_admin === "boolean" ? b.is_admin : !!appIsAdmin;
      const premium = typeof b.is_premium === "boolean" ? b.is_premium : !!appIsPremium;
      setIsAdmin(admin);
      setIsPremium(premium);
      if (b.credits_remaining !== undefined) {
        const cr = b.credits_remaining;
        setCreditsRemaining(typeof cr === "number" ? cr : cr == null ? null : Number(cr));
      }
      if (b.credits_limit !== undefined) {
        const cl = b.credits_limit;
        setCreditsLimit(typeof cl === "number" ? cl : cl == null ? null : Number(cl));
      }
    } catch { }
    setUserInfoLoaded(true);
  }, [bootstrap, appFtp, appIsAdmin, appIsPremium]);

  const openPremiumModal = useCallback((context) => {
    try {
      premiumModalRef.current?.open?.({ context: context ? String(context) : "" });
    } catch {
      // ignore
    }
  }, []);

  const premiumBtnStyle = useMemo(
    () => ({
      background: "#faad14",
      borderColor: "#faad14",
      color: "#111",
      fontWeight: 600,
    }),
    []
  );

  const isOutOfCredits = useMemo(() => {
    if (isAdmin) return false;
    if (isPremium) return false;
    if (creditsRemaining == null) return false; // unknown: let backend decide
    return Number(creditsRemaining) <= 0;
  }, [creditsRemaining, isAdmin, isPremium]);

  const creditsLabel = useMemo(() => {
    if (isPremium) return "Unlimited credits";
    if (creditsRemaining == null) return "Monthly credits remaining: -";
    return `Monthly credits remaining: ${Math.max(0, Number(creditsRemaining) || 0)}`;
  }, [creditsRemaining, isPremium]);

  const handleAiError = useCallback(
    (err) => {
      try {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = (detail && typeof detail === "object" ? detail.code : null) || err?.response?.data?.code || null;

        if (status === 429 && code === "ai_credits_exhausted") {
          const cr = detail && typeof detail === "object" ? detail.credits_remaining : null;
          const cl = detail && typeof detail === "object" ? detail.credits_limit : null;
          if (typeof cr === "number") setCreditsRemaining(cr);
          if (typeof cl === "number") setCreditsLimit(cl);
          // Friendly UX: no raw backend/GPT error strings.
          openPremiumModal("AI credits used up (Simpl'AI Plan Builder)");
          return true;
        }
      } catch {
        // ignore
      }
      return false;
    },
    [openPremiumModal]
  );

  const canShowWatts = useMemo(() => typeof displayFtp === "number" && displayFtp > 10, [displayFtp]);

  const powerPercentToWatts = useCallback(
    (p) => {
      const pct = typeof p === "number" ? p : Number(p);
      if (!Number.isFinite(pct)) return "";
      if (!canShowWatts) return "";
      return Math.round((pct * displayFtp) / 100);
    },
    [canShowWatts, displayFtp]
  );

  const powerWattsToPercent = useCallback(
    (w) => {
      const watts = typeof w === "number" ? w : Number(w);
      if (!Number.isFinite(watts)) return 0;
      if (!canShowWatts) return 0;
      return Math.round((watts * 100) / displayFtp);
    },
    [canShowWatts, displayFtp]
  );

  // Step validation rules
  const STEP_RULES = useMemo(
    () => ({
      minSteps: 1,
      maxSteps: 100,
      durationMinSec: 10,
      durationMaxSec: 10 * 3600,
      rpmMin: 10,
      rpmMax: 300,
      wattsMin: 10,
      wattsMax: 1500,
      pctMin: 5,
      pctMax: 1000,
    }),
    []
  );

  const parseIntStrict = useCallback((s) => {
    const raw = typeof s === "string" ? s.trim() : s == null ? "" : String(s).trim();
    if (!raw) return { ok: false, value: null };
    const n = Number(raw);
    if (!Number.isFinite(n)) return { ok: false, value: null };
    const i = Math.trunc(n);
    return { ok: true, value: i };
  }, []);

  const getDraft = useCallback(
    (sessionKey, stepIdx, field) => {
      const root = stepDraftsBySession && typeof stepDraftsBySession === "object" ? stepDraftsBySession : {};
      const ses = root[sessionKey] && typeof root[sessionKey] === "object" ? root[sessionKey] : null;
      const row = ses && ses[stepIdx] && typeof ses[stepIdx] === "object" ? ses[stepIdx] : null;
      return row && Object.prototype.hasOwnProperty.call(row, field) ? row[field] : undefined;
    },
    [stepDraftsBySession]
  );

  const setDraft = useCallback((sessionKey, stepIdx, field, value) => {
    setStepDraftsBySession((prev) => {
      const next = { ...(prev || {}) };
      const ses = { ...((next[sessionKey] && typeof next[sessionKey] === "object" ? next[sessionKey] : {}) || {}) };
      const row = { ...((ses[stepIdx] && typeof ses[stepIdx] === "object" ? ses[stepIdx] : {}) || {}) };
      row[field] = value;
      ses[stepIdx] = row;
      next[sessionKey] = ses;
      return next;
    });
  }, []);

  const clearDraft = useCallback((sessionKey, stepIdx, field) => {
    setStepDraftsBySession((prev) => {
      const cur = prev && typeof prev === "object" ? prev : {};
      const ses0 = cur[sessionKey] && typeof cur[sessionKey] === "object" ? cur[sessionKey] : null;
      if (!ses0) return prev;
      const row0 = ses0[stepIdx] && typeof ses0[stepIdx] === "object" ? ses0[stepIdx] : null;
      if (!row0) return prev;
      if (!Object.prototype.hasOwnProperty.call(row0, field)) return prev;

      const next = { ...cur };
      const ses = { ...ses0 };
      const row = { ...row0 };
      delete row[field];
      if (Object.keys(row).length === 0) {
        delete ses[stepIdx];
      } else {
        ses[stepIdx] = row;
      }
      if (Object.keys(ses).length === 0) {
        delete next[sessionKey];
      } else {
        next[sessionKey] = ses;
      }
      return next;
    });
  }, []);

  const clearSessionDrafts = useCallback((sessionKey) => {
    setStepDraftsBySession((prev) => {
      const cur = prev && typeof prev === "object" ? prev : {};
      if (!Object.prototype.hasOwnProperty.call(cur, sessionKey)) return prev;
      const next = { ...cur };
      delete next[sessionKey];
      return next;
    });
  }, []);

  const validatePercent = useCallback(
    (pct) => {
      if (!Number.isFinite(pct)) return `Enter a number (${STEP_RULES.pctMin}–${STEP_RULES.pctMax}% FTP)`;
      if (pct < STEP_RULES.pctMin || pct > STEP_RULES.pctMax) return `Must be ${STEP_RULES.pctMin}–${STEP_RULES.pctMax}%`;
      return "";
    },
    [STEP_RULES]
  );

  const validateWatts = useCallback(
    (w) => {
      if (!Number.isFinite(w)) return `Enter a number (${STEP_RULES.wattsMin}–${STEP_RULES.wattsMax}W)`;
      if (w < STEP_RULES.wattsMin || w > STEP_RULES.wattsMax) return `Must be ${STEP_RULES.wattsMin}–${STEP_RULES.wattsMax}W`;
      return "";
    },
    [STEP_RULES]
  );

  const validateDuration = useCallback(
    (sec) => {
      if (!Number.isFinite(sec)) return `Enter seconds (${STEP_RULES.durationMinSec}–${STEP_RULES.durationMaxSec})`;
      if (sec < STEP_RULES.durationMinSec || sec > STEP_RULES.durationMaxSec)
        return `Must be ${STEP_RULES.durationMinSec}s–${STEP_RULES.durationMaxSec}s`;
      return "";
    },
    [STEP_RULES]
  );

  const validateRpm = useCallback(
    (rpm) => {
      if (!Number.isFinite(rpm)) return `Enter RPM (${STEP_RULES.rpmMin}–${STEP_RULES.rpmMax}) or leave empty`;
      if (rpm === 0) return ""; // "none"
      if (rpm < STEP_RULES.rpmMin || rpm > STEP_RULES.rpmMax) return `Must be ${STEP_RULES.rpmMin}–${STEP_RULES.rpmMax} RPM`;
      return "";
    },
    [STEP_RULES]
  );

  const getFieldError = useCallback(
    (sessionKey, stepIdx, field, workout) => {
      const steps = Array.isArray(workout?.steps) ? workout.steps : [];
      const st = steps[stepIdx] || {};

      if (field === "duration") {
        const d = getDraft(sessionKey, stepIdx, "duration");
        const src = d !== undefined ? d : st?.duration;
        const parsed = parseIntStrict(src);
        return validateDuration(parsed.ok ? parsed.value : NaN);
      }
      if (field === "rpm") {
        const d = getDraft(sessionKey, stepIdx, "rpm");
        const src = d !== undefined ? d : st?.rpm;
        // Optional: allow empty/0 as "none"
        if (src == null) return "";
        if (typeof src === "string" && !src.trim()) return "";
        const parsed = parseIntStrict(src);
        if (parsed.ok && parsed.value === 0) return "";
        return validateRpm(parsed.ok ? parsed.value : NaN);
      }
      if (field === "watt") {
        const pctStored = Number(st?.watt);
        const pctErr = validatePercent(Number.isFinite(pctStored) ? pctStored : NaN);
        const d = getDraft(sessionKey, stepIdx, "watt");

        if (d !== undefined) {
          const parsed = parseIntStrict(d);
          if (!parsed.ok) return powerDisplayMode === "watts" ? `Enter Watts (${STEP_RULES.wattsMin}–${STEP_RULES.wattsMax})` : `Enter %FTP (${STEP_RULES.pctMin}–${STEP_RULES.pctMax})`;
          if (powerDisplayMode === "watts") {
            if (!canShowWatts) return "Enter Display FTP to edit in Watts";
            const wErr = validateWatts(parsed.value);
            if (wErr) return wErr;
            const pct = powerWattsToPercent(parsed.value);
            const pctFromWErr = validatePercent(Number.isFinite(pct) ? pct : NaN);
            return pctFromWErr || "";
          }
          const pctErr2 = validatePercent(parsed.value);
          if (pctErr2) return pctErr2;
          if (canShowWatts) {
            const w = powerPercentToWatts(parsed.value);
            const wErr2 = validateWatts(Number(w));
            if (wErr2) return wErr2;
          }
          return "";
        }

        if (pctErr) return pctErr;
        if (canShowWatts) {
          const w = powerPercentToWatts(pctStored);
          const wErr = validateWatts(Number(w));
          if (wErr) return wErr;
        }
        return "";
      }
      return "";
    },
    [
      STEP_RULES,
      canShowWatts,
      getDraft,
      parseIntStrict,
      powerDisplayMode,
      powerPercentToWatts,
      powerWattsToPercent,
      validateDuration,
      validatePercent,
      validateRpm,
      validateWatts,
    ]
  );

  const ensureUniqueCategory = (base, existing) => {
    const ex = new Set((existing || []).map((x) => String(x || "").trim().toLowerCase()).filter(Boolean));
    const cleanBase = String(base || "").trim() || "Simpl'AI Plan Builder";
    if (!ex.has(cleanBase.toLowerCase())) return cleanBase;
    for (let i = 1; i < 200; i++) {
      const cand = `${cleanBase}-ai${i === 1 ? "" : i}`;
      if (!ex.has(cand.toLowerCase())) return cand;
    }
    return `${cleanBase}-ai-${Date.now()}`;
  };

  // Fetch existing categories for this user (to enforce "new category only")
  useEffect(() => {
    if (fetchedCategoriesRef.current) return;
    fetchedCategoriesRef.current = true;
    (async () => {
      try {
        const resp = await axiosInstance.post("/get_all_training_plans/", {
          exclude_steps: true,
          limit: 10000,
        });
        const plans = Array.isArray(resp?.data?.plans_info) ? resp.data.plans_info : [];
        const cats = Array.from(
          new Set(
            plans
              .map((p) => (p && typeof p.category === "string" ? p.category.trim() : ""))
              .filter(Boolean)
          )
        );
        setExistingCategories(cats);
      } catch (e) {
        setExistingCategories([]);
      } finally {
        setCategoriesLoaded(true);
      }
    })();
  }, [axiosInstance]);

  // Compute default program category (must be NEW). Prefer the GPT blueprint category/name as base.
  useEffect(() => {
    if (currentStep < 1) return; // only Step 2+ cares
    if (!blueprint) return;
    if (!categoriesLoaded) return; // wait until we can check uniqueness
    if (didAutoSeedCategoryRef.current) return; // seed only once; do not re-fill if user clears it
    if (programCategory.trim()) return;
    const base = blueprint?.plan_category || blueprint?.plan_name || "Simpl'AI Plan Builder";
    const next = ensureUniqueCategory(base, existingCategories);
    setProgramCategory(next);
    didAutoSeedCategoryRef.current = true;
    emitPersistState({
      status: "in_progress",
      current_step: currentStep,
      blueprint,
      program_category: next,
    });
  }, [currentStep, blueprint, existingCategories, programCategory, categoriesLoaded, emitPersistState]);

  // Validate category: must be non-empty AND not in user's existing category list.
  // Debounced by 0.2s to allow smooth typing.
  useEffect(() => {
    if (currentStep < 1) {
      setCategoryChecking(false);
      setCategoryError("");
      return;
    }

    const raw = String(programCategory || "");
    // Allow empty while typing: don't show an error and don't keep the "checking" spinner.
    if (!raw.trim()) {
      setCategoryChecking(false);
      setCategoryError("");
      return;
    }

    setCategoryChecking(true);
    const t = setTimeout(() => {
      const c = raw.trim();
      if (!categoriesLoaded) {
        // Keep the UI in "checking" until categories are fetched.
        setCategoryError("");
        setCategoryChecking(true);
        return;
      }
      const exists = (existingCategories || []).some((x) => String(x || "").trim().toLowerCase() === c.toLowerCase());
      if (exists) {
        setCategoryError("This category already exists in your library. Please choose a new one.");
        setCategoryChecking(false);
        return;
      }
      setCategoryError("");
      setCategoryChecking(false);
    }, 200);
    return () => clearTimeout(t);
  }, [programCategory, existingCategories, categoriesLoaded, currentStep]);

  const sendCoachMessage = async () => {
    const userText = (chatInput || "").trim();
    if (!userText) return;
    if (isOutOfCredits) {
      openPremiumModal("Simpl'AI Plan Builder - Step 1 (Coach Intake) - out of credits");
      return;
    }

    const nextMessages = [...chatMessages, { role: "user", content: userText }];
    setChatMessages(nextMessages);
    setChatInput("");
    setSending(true);

    try {
      const resp = await axiosInstance.post("/ai/plan_builder/step1/", {
        user_message: userText,
        conversation: nextMessages,
        last_reformulation: lastReformulation || undefined,
      });

      const data = resp?.data || {};
      // Keep credits display fresh (backend returns remaining credits when available).
      try {
        if (typeof data?.credits_remaining === "number") setCreditsRemaining(data.credits_remaining);
        if (typeof data?.credits_limit === "number") setCreditsLimit(data.credits_limit);
        if (typeof data?.is_premium === "boolean") setIsPremium(data.is_premium);
      } catch {}
      const reformulated = data.reformulated_desiderata || "";
      const enough = !!data.is_enough;
      const whatsMissing = (data.whats_missing || "").trim();
      const dbg = data.debug ? capDebug(data.debug) : null;
      const adminForThis = isAdmin || !!dbg;
      if (dbg) setIsAdmin(true);

      // Always store reformulation (even if we don't show it when not enough)
      setLastReformulation(reformulated);
      setIsEnough(enough);
      if (dbg) setDebugStep1(dbg);

      const assistantText = enough
        ? `Here is your desiderata (reformulated):\n${reformulated}\n\nLet’s plan your program → go to Step 2.\n\nOptional refinements / advice:\n${whatsMissing || "- Add anything else you want me to consider."}\n\nYou can also add another message to refine it.`
        : (whatsMissing || "Add more info.");

      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: assistantText,
          meta: {
            is_enough: enough,
            whats_missing: whatsMissing,
            reformulated_desiderata: reformulated,
          },
        },
      ]);

      emitPersistState({
        status: "in_progress",
        current_step: 0,
        conversation: [
          ...nextMessages,
          {
            role: "assistant",
            content: assistantText,
          },
        ],
        last_reformulation: reformulated,
        is_enough: enough,
        whats_missing: whatsMissing,
        debug: adminForThis
          ? {
              step1: dbg || debugStep1,
              step2: debugStep2,
              sessions: sessionDebug,
            }
          : undefined,
      });
    } catch (err) {
      console.error("Simpl'AI plan builder step1 error:", err);
      if (!handleAiError(err)) message.error("Couldn’t reach the AI right now. Please try again.");
      // keep the user message in chat, but don't advance state
    } finally {
      setSending(false);
    }
  };

  // Auto-scroll conversation to newest message
  useEffect(() => {
    if (!convoScrollRef.current) return;
    try {
      convoScrollRef.current.scrollTop = convoScrollRef.current.scrollHeight;
    } catch (e) {
      // ignore
    }
  }, [chatMessages.length]);

  // Keep App-level "last builder" state fresh while the modal is open (so closing/reopening can resume without relogin).
  useEffect(() => {
    if (typeof onPersistState !== "function") return;
    const t = setTimeout(() => {
      emitPersistState();
    }, 250);
    return () => clearTimeout(t);
  }, [onPersistState, emitPersistState, currentStep, chatMessages, lastReformulation, isEnough, blueprint, programCategory, sessionWorkouts, sessionComments, sessionNameOverrides]);

  // When entering Step 2, auto-generate a blueprint once (if we have desiderata).
  useEffect(() => {
    if (currentStep !== 1) return;
    if (blueprint || blueprintLoading) return;
    if (!lastReformulation.trim()) return;
    if (isOutOfCredits) return;
    (async () => {
      setBlueprintLoading(true);
      setLastBlueprintError("");
      try {
        const resp = await axiosInstance.post("/ai/plan_builder/step2/", {
          reformulated_desiderata: lastReformulation,
        });
        const bp = resp?.data?.blueprint;
        try {
          if (typeof resp?.data?.credits_remaining === "number") setCreditsRemaining(resp.data.credits_remaining);
          if (typeof resp?.data?.credits_limit === "number") setCreditsLimit(resp.data.credits_limit);
          if (typeof resp?.data?.is_premium === "boolean") setIsPremium(resp.data.is_premium);
        } catch {}
        const dbg = resp?.data?.debug ? capDebug(resp.data.debug) : null;
        const adminForThis = isAdmin || !!dbg;
        if (dbg) setIsAdmin(true);
        if (dbg) setDebugStep2(dbg);
        if (!bp) throw new Error("Missing blueprint in response");
        setBlueprint(bp);
        emitPersistState({
          status: "in_progress",
          current_step: 1,
          blueprint: bp,
          debug: adminForThis
            ? {
                step1: debugStep1,
                step2: dbg || debugStep2,
                sessions: sessionDebug,
              }
            : undefined,
        });
      } catch (e) {
        console.error("Simpl'AI plan builder step2 auto-generate error:", e);
        if (handleAiError(e)) {
          setLastBlueprintError("You’ve used all your AI credits for this month. Try again next month or upgrade to Premium.");
        } else {
          setLastBlueprintError("Failed to generate blueprint. Please try again.");
        }
      } finally {
        setBlueprintLoading(false);
      }
    })();
  }, [currentStep, blueprint, blueprintLoading, lastReformulation, axiosInstance, emitPersistState, isAdmin, capDebug, debugStep1, debugStep2, sessionDebug, isOutOfCredits, handleAiError]);

  // If the blueprint changes (regenerated/refined), clear built sessions to avoid mismatches.
  useEffect(() => {
    if (skipNextBlueprintClearRef.current) {
      skipNextBlueprintClearRef.current = false;
      return;
    }
    setSessionWorkouts({});
    setSessionComments({});
    setStepDraftsBySession({});
    setBatchFailures([]);
    setInitialBatchDone(false);
    autoBatchRanRef.current = false;
    setSessionDebug({});
  }, [blueprint]);

  const getSessionKey = (weekNumber, sessionIndex) => `${weekNumber}:${sessionIndex}`;
  const sessionPrefix = (weekNumber, sessionIndex) => `Week ${weekNumber} Session ${sessionIndex + 1}`;
  const composeSessionDisplayTitle = (weekNumber, sessionIndex, rawTitle) => {
    const prefix = sessionPrefix(weekNumber, sessionIndex);
    const t = String(rawTitle || "").trim();
    if (!t) return prefix;
    if (t.toLowerCase().startsWith(prefix.toLowerCase())) return t; // already prefixed
    return `${prefix} - ${t}`;
  };

  const computeWorkoutTotalSeconds = (workout) => {
    if (!workout || !Array.isArray(workout.steps)) return 0;
    return workout.steps.reduce((acc, st) => acc + (parseInt(st?.duration, 10) || 0), 0);
  };

  const computeWorkoutDurationMinutes = (workout) => {
    const totalSec = computeWorkoutTotalSeconds(workout);
    if (!totalSec) return 0;
    return Math.round(totalSec / 60);
  };

  const computeWorkoutTss = (workout) => {
    const raw = workout?.stress_points;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const totalSessionsCount = useMemo(() => {
    return (blueprint?.weeks || []).reduce((acc, w) => acc + ((w?.sessions || []).length || 0), 0);
  }, [blueprint]);

  // Program duration helpers (minutes)
  const getSessionExpectedSecondsFromBlueprint = useCallback((session) => {
    try {
      const minM = typeof session?.duration_min === "number" ? session.duration_min : Number(session?.duration_min);
      const maxM = typeof session?.duration_max === "number" ? session.duration_max : Number(session?.duration_max);
      const hasMin = Number.isFinite(minM) && minM > 0;
      const hasMax = Number.isFinite(maxM) && maxM > 0;
      if (hasMin && hasMax) return Math.round(((minM + maxM) / 2) * 60);
      if (hasMin) return Math.round(minM * 60);
      if (hasMax) return Math.round(maxM * 60);
      return 0;
    } catch {
      return 0;
    }
  }, []);

  const plannedProgramSeconds = useMemo(() => {
    try {
      const weeks = Array.isArray(blueprint?.weeks) ? blueprint.weeks : [];
      let total = 0;
      for (const w of weeks) {
        const sessions = Array.isArray(w?.sessions) ? w.sessions : [];
        for (const s of sessions) total += getSessionExpectedSecondsFromBlueprint(s);
      }
      return total;
    } catch {
      return 0;
    }
  }, [blueprint, getSessionExpectedSecondsFromBlueprint]);

  const builtProgramSeconds = useMemo(() => {
    try {
      const workouts = sessionWorkouts && typeof sessionWorkouts === "object" ? sessionWorkouts : {};
      let total = 0;
      for (const wk of Object.values(workouts || {})) {
        if (!wk) continue;
        total += computeWorkoutTotalSeconds(wk);
      }
      return total;
    } catch {
      return 0;
    }
  }, [sessionWorkouts]);

  const effectiveProgramSeconds = useMemo(() => {
    // Prefer built duration for sessions that exist; otherwise fall back to blueprint estimates.
    try {
      const weeks = Array.isArray(blueprint?.weeks) ? blueprint.weeks : [];
      const workouts = sessionWorkouts && typeof sessionWorkouts === "object" ? sessionWorkouts : {};
      let total = 0;
      for (const w of weeks) {
        const sessions = Array.isArray(w?.sessions) ? w.sessions : [];
        for (let i = 0; i < sessions.length; i++) {
          const key = getSessionKey(w.week_number, i);
          const wk = workouts?.[key];
          total += wk ? computeWorkoutTotalSeconds(wk) : getSessionExpectedSecondsFromBlueprint(sessions[i]);
        }
      }
      return total;
    } catch {
      return 0;
    }
  }, [blueprint, sessionWorkouts, getSessionExpectedSecondsFromBlueprint]);

  const hasAnyGeneratedWorkout = useCallback((workoutsObj) => {
    try {
      const obj = workoutsObj && typeof workoutsObj === "object" ? workoutsObj : {};
      return Object.values(obj).some(Boolean);
    } catch {
      return false;
    }
  }, []);

  // --- Leave / draft dirty-check (manual edits) -----------------------------------
  const draftBaselineRef = useRef(null); // JSON string snapshot
  const baselineReadyRef = useRef(false);
  const baselineAutoResetRef = useRef(false);

  const currentDraftSnapshot = useMemo(() => {
    try {
      const cat = String(programCategory || "").trim();

      const sw = sessionWorkouts && typeof sessionWorkouts === "object" ? sessionWorkouts : {};
      const sc = sessionComments && typeof sessionComments === "object" ? sessionComments : {};
      const sn = sessionNameOverrides && typeof sessionNameOverrides === "object" ? sessionNameOverrides : {};
      const sd = stepDraftsBySession && typeof stepDraftsBySession === "object" ? stepDraftsBySession : {};

      const sessionKeys = Object.keys(sw).sort((a, b) => String(a).localeCompare(String(b)));
      const sessionsOut = {};
      sessionKeys.forEach((k) => {
        const w = sw[k];
        if (!w || typeof w !== "object") return;
        const steps = Array.isArray(w.steps) ? w.steps : [];
        const normSteps = steps.map((st) => ({
          title: String(st?.title || "").trim(),
          comment: String(st?.comment || "").trim(),
          duration: Number(st?.duration) || 0,
          watt: Number(st?.watt) || 0,
          rpm: Number(st?.rpm) || 0,
        }));
        sessionsOut[k] = {
          title: String(w?.title || "").trim(),
          description: String(w?.description || "").trim(),
          steps: normSteps,
        };
      });

      const sortMapKeys = (m) => {
        const out = {};
        Object.keys(m || {})
          .sort((a, b) => String(a).localeCompare(String(b)))
          .forEach((k) => {
            out[k] = m[k];
          });
        return out;
      };

      return JSON.stringify({
        program_category: cat,
        sessions: sessionsOut,
        session_comments: sortMapKeys(sc),
        session_name_overrides: sortMapKeys(sn),
        step_drafts_by_session: sortMapKeys(sd),
      });
    } catch {
      return "";
    }
  }, [programCategory, sessionComments, sessionNameOverrides, sessionWorkouts, stepDraftsBySession]);

  const setDraftBaselineToCurrent = useCallback(() => {
    if (!currentDraftSnapshot) return;
    draftBaselineRef.current = currentDraftSnapshot;
    baselineReadyRef.current = true;
  }, [currentDraftSnapshot]);

  useEffect(() => {
    // Establish baseline once we have something editable (blueprint and/or generated sessions),
    // so Leave doesn't prompt immediately.
    if (baselineReadyRef.current) return;
    if (!blueprint && !hasAnyGeneratedWorkout(sessionWorkouts)) return;
    if (!currentDraftSnapshot) return;
    setDraftBaselineToCurrent();
  }, [blueprint, currentDraftSnapshot, hasAnyGeneratedWorkout, sessionWorkouts, setDraftBaselineToCurrent]);

  const requestBaselineReset = useCallback(() => {
    baselineAutoResetRef.current = true;
  }, []);

  useEffect(() => {
    if (!baselineAutoResetRef.current) return;
    if (!currentDraftSnapshot) return;
    baselineAutoResetRef.current = false;
    setDraftBaselineToCurrent();
  }, [currentDraftSnapshot, setDraftBaselineToCurrent]);

  const doLeaveNoPrompt = useCallback(() => {
    try {
      emitPersistState();
    } catch {
      // ignore
    }
    try {
      if (typeof onClose === "function") onClose();
    } catch {
      // ignore
    }
  }, [emitPersistState, onClose]);

  const saveDraft = useCallback(
    async ({ closeAfter = false } = {}) => {
      setSavingDraft(true);
      try {
        // Always persist locally (App state + localStorage via onPersistState)
        emitPersistState({
          status: "in_progress",
          current_step: currentStep,
        });

        // Explicit remote save (Firestore) – only on user action
        const cacheId = String(programCategory || "").trim();
        const workouts = sessionWorkouts && typeof sessionWorkouts === "object" ? sessionWorkouts : {};
        const any = Object.values(workouts || {}).some(Boolean);

        if (cacheId && any) {
          const sessionsPayload = Object.entries(workouts || {})
            .filter(([, wkt]) => !!wkt)
            .map(([k, wkt]) => ({
              key: k,
              workout: wkt,
              comment: (sessionComments || {})[k] || undefined,
              name_override: (sessionNameOverrides || {})[k] || undefined,
            }));
          if (sessionsPayload.length) {
            await axiosInstance.post("/ai/plan_builder/session_cache/upsert_batch/", {
              cache_id: cacheId,
              program_category: cacheId,
              sessions: sessionsPayload,
            });
          }
        }

        await axiosInstance.post("/ai/plan_builder/save_draft/", {
          status: "in_progress",
          current_step: currentStep,
          conversation: (chatMessages || [])
            .map((m) => ({ role: m?.role, content: m?.content }))
            .filter((m) => (m.role === "user" || m.role === "assistant") && typeof m.content === "string"),
          last_reformulation: lastReformulation || null,
          is_enough: typeof isEnough === "boolean" ? isEnough : null,
          blueprint: blueprint || null,
          program_category: String(programCategory || "").trim() || null,
          step_drafts_by_session: stepDraftsBySession || {},
        });

        // After successful draft save, clear "dirty" state.
        setDraftBaselineToCurrent();
        message.success("Saved draft. You can resume editing later.");
        if (closeAfter) doLeaveNoPrompt();
      } catch (e) {
        console.error("Save Simpl'AI plan builder draft error:", e);
        message.error(e?.response?.data?.detail || e?.message || "Failed to save draft");
      } finally {
        setSavingDraft(false);
      }
    },
    [
      axiosInstance,
      blueprint,
      chatMessages,
      currentStep,
      doLeaveNoPrompt,
      emitPersistState,
      isEnough,
      lastReformulation,
      programCategory,
      sessionComments,
      sessionNameOverrides,
      sessionWorkouts,
      setDraftBaselineToCurrent,
      stepDraftsBySession,
    ]
  );

  const resumeEditingLater = useCallback(async () => {
    await saveDraft({ closeAfter: true });
  }, [saveDraft]);

  const handleLeave = useCallback(() => {
    // Always implicit: leaving behaves like "Resume editing later"
    saveDraft({ closeAfter: true });
  }, [saveDraft]);

  // Step 3 resume: if workouts aren't in app state (e.g. after refresh), try restoring them from session cache.
  useEffect(() => {
    if (currentStep !== 2) return;
    if (!blueprint) return;
    if (categoryChecking) return;
    if (categoryError) return;
    const cacheId = String(programCategory || "").trim();
    if (!cacheId) return;
    if (step3RemoteRestoreAttemptedRef.current) return;
    if (hasAnyGeneratedWorkout(sessionWorkouts)) {
      step3RemoteRestoreAttemptedRef.current = true;
      return;
    }

    step3RemoteRestoreAttemptedRef.current = true;
    (async () => {
      try {
        const resp = await axiosInstance.post("/ai/plan_builder/session_cache/get/", { cache_id: cacheId });
        const sw = resp?.data?.session_workouts && typeof resp.data.session_workouts === "object" ? resp.data.session_workouts : {};
        const sc = resp?.data?.session_comments && typeof resp.data.session_comments === "object" ? resp.data.session_comments : {};
        const sn = resp?.data?.session_name_overrides && typeof resp.data.session_name_overrides === "object" ? resp.data.session_name_overrides : {};

        if (hasAnyGeneratedWorkout(sw)) {
          setSessionWorkouts(sw);
          setSessionComments(sc);
          setSessionNameOverrides(sn);
          setStepDraftsBySession({});
          setInitialBatchDone(true);
          // Prevent auto-batch after we restored from cache
          autoBatchRanRef.current = true;
          step3AutoBatchEligibleRef.current = false;

          emitPersistState({
            status: "in_progress",
            current_step: 2,
            blueprint,
            program_category: cacheId,
            session_workouts: sw,
            session_comments: sc,
            session_name_overrides: sn,
          });
          // This is a restore from remote cache (not a manual edit) → don't nag on Leave.
          requestBaselineReset();
        }
      } catch (e) {
        // ignore: if cache doesn't exist/fails, auto-batch can proceed (when eligible)
      }
    })();
  }, [axiosInstance, blueprint, categoryChecking, categoryError, currentStep, emitPersistState, hasAnyGeneratedWorkout, programCategory, requestBaselineReset, sessionWorkouts]);

  // Auto-generate all sessions when either:
  // - transitioning from Step 2 -> Step 3 (always), or
  // - resuming directly in Step 3 with no saved sessions.
  useEffect(() => {
    const prev = prevStepRef.current;
    prevStepRef.current = currentStep;
    if (currentStep !== 2) return;

    // If transitioning from Step 2 -> Step 3, we want to auto-generate all sessions.
    // IMPORTANT: the category may become valid *after* the first Step 3 render, so we
    // keep an "eligible" flag until the batch actually runs.
    if (prev === 1) {
      step3AutoBatchEligibleRef.current = true;
      autoBatchRanRef.current = false;
      step3RemoteRestoreAttemptedRef.current = true; // don't block generation on a remote restore attempt
      setInitialBatchDone(false);
      setBatchFailures([]);
      // Always regenerate from scratch on Step 3 entry from Step 2
      setSessionWorkouts({});
      setSessionComments({});
      setStepDraftsBySession({});
    }

    if (!step3AutoBatchEligibleRef.current) return;
    if (!blueprint) return;
    if (categoryChecking) return;
    if (categoryError) return;
    if (isOutOfCredits) {
      // Avoid leaving the user stuck on "Generating..." forever.
      setInitialBatchDone(true);
      return;
    }
    if (autoBatchRanRef.current) return;
    // Give the Step 3 resume cache loader a chance before we auto-generate.
    if (!step3RemoteRestoreAttemptedRef.current) return;
    if (totalSessionsCount <= 0) return;

    autoBatchRanRef.current = true;
    (async () => {
      setBatchGenerating(true);
      setInitialBatchDone(false);
      setBatchFailures([]);
      try {
        const resp = await axiosInstance.post("/ai/plan_builder/step3_batch/", {
          blueprint,
          // Allow generation even when category is empty; backend will fall back to blueprint category.
          program_category: programCategory.trim() || undefined,
          // Carry-forward: ensure all sessions see the Step 1 reformulation (global requirements/constraints).
          reformulated_desiderata: lastReformulation || undefined,
        });
        try {
          if (typeof resp?.data?.credits_remaining === "number") setCreditsRemaining(resp.data.credits_remaining);
          if (typeof resp?.data?.credits_limit === "number") setCreditsLimit(resp.data.credits_limit);
          if (typeof resp?.data?.is_premium === "boolean") setIsPremium(resp.data.is_premium);
        } catch {}
        const results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        const nextWorkouts = {};
        const failures = [];
        const nextDebug = {};
        let sawDebug = false;
        for (const r of results) {
          if (r && r.ok && r.key && r.workout) {
            const wk = r.workout;
            nextWorkouts[r.key] = {
              ...wk,
              category: programCategory.trim() || wk.category,
            };
            if (r.debug) {
              nextDebug[r.key] = capDebug(r.debug);
              sawDebug = true;
            }
          }
          else if (r && r.key && !r.ok) {
            failures.push({ key: r.key, error: r.error || "Failed" });
            if (r.debug) {
              nextDebug[r.key] = capDebug(r.debug);
              sawDebug = true;
            }
          }
        }
        setSessionWorkouts(nextWorkouts);
        setStepDraftsBySession({});
        setBatchFailures(failures);
        if (sawDebug) setIsAdmin(true);
        if (Object.keys(nextDebug || {}).length) {
          setSessionDebug((prev) => ({ ...(prev || {}), ...(nextDebug || {}) }));
        }
        // This is AI generation (not a manual edit) → don't nag on Leave.
        requestBaselineReset();
        // Persist immediately so closing/reopening resumes with generated sessions.
        emitPersistState({
          status: "in_progress",
          current_step: 2,
          blueprint,
          program_category: programCategory.trim(),
          session_workouts: nextWorkouts,
          debug: (isAdmin || sawDebug)
            ? {
                step1: debugStep1,
                step2: debugStep2,
                sessions: { ...(sessionDebug || {}), ...(nextDebug || {}) },
              }
            : undefined,
        });
        if (resp?.data?.warning) {
          message.warning(String(resp.data.warning));
        }
        else if (failures.length > 0) {
          message.warning(`Generated ${Object.keys(nextWorkouts).length}/${results.length}. ${failures.length} failed.`);
          console.error("step3_batch failures:", failures);
        } else {
          message.success(`Generated ${Object.keys(nextWorkouts).length} workouts.`);
        }
      } catch (e) {
        console.error("step3_batch error:", e);
        if (!handleAiError(e)) message.error("Failed to generate workouts. Please try again.");
        // allow retry by leaving autoBatchRanRef false
        autoBatchRanRef.current = false;
      } finally {
        setBatchGenerating(false);
        setInitialBatchDone(true);
      }
    })();
  }, [axiosInstance, blueprint, categoryChecking, categoryError, currentStep, programCategory, totalSessionsCount, emitPersistState, sessionComments, sessionNameOverrides, lastReformulation, isAdmin, capDebug, debugStep1, debugStep2, sessionDebug, requestBaselineReset, isOutOfCredits, handleAiError]);

  const refineBlueprint = async () => {
    if (!lastReformulation.trim()) {
      message.error("Missing reformulated desiderata. Please complete Step 1 first.");
      return;
    }
    if (isOutOfCredits) {
      openPremiumModal("Simpl'AI Plan Builder - Step 2 (Blueprint) - out of credits");
      return;
    }
    setBlueprintLoading(true);
    setLastBlueprintError("");
    try {
      const resp = await axiosInstance.post("/ai/plan_builder/step2/", {
        reformulated_desiderata: lastReformulation,
        comment: blueprintRefineText.trim() || undefined,
        previous_blueprint: blueprint || undefined,
      });
      const bp = resp?.data?.blueprint;
      try {
        if (typeof resp?.data?.credits_remaining === "number") setCreditsRemaining(resp.data.credits_remaining);
        if (typeof resp?.data?.credits_limit === "number") setCreditsLimit(resp.data.credits_limit);
        if (typeof resp?.data?.is_premium === "boolean") setIsPremium(resp.data.is_premium);
      } catch {}
      const dbg = resp?.data?.debug ? capDebug(resp.data.debug) : null;
      const adminForThis = isAdmin || !!dbg;
      if (dbg) setIsAdmin(true);
      if (dbg) setDebugStep2(dbg);
      if (!bp) throw new Error("Missing blueprint in response");
      setBlueprint(bp);
      setBlueprintRefineText("");
      emitPersistState({
        status: "in_progress",
        current_step: 1,
        blueprint: bp,
        debug: adminForThis
          ? {
              step1: debugStep1,
              step2: dbg || debugStep2,
              sessions: sessionDebug,
            }
          : undefined,
      });
    } catch (e) {
      console.error("Simpl'AI plan builder step2 refine error:", e);
      if (handleAiError(e)) {
        setLastBlueprintError("You’ve used all your AI credits for this month. Try again next month or upgrade to Premium.");
      } else {
        setLastBlueprintError("Failed to refine blueprint. Please try again.");
      }
    } finally {
      setBlueprintLoading(false);
    }
  };

  const Step1 = (
    <Space direction="vertical" style={{ width: "100%" }} size={12}>
      <Alert
        type="info"
        showIcon
        message="Tell the coach what you want"
        description="Describe your goal, your level, how many weeks, days per week, available time per session, equipment, and any constraints. The coach will reformulate it and ask for missing info."
      />

      <Card size="small" title="Conversation">
        {chatMessages.length === 0 ? (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Start by describing your desired training program.
          </Paragraph>
        ) : (
          <div
            ref={convoScrollRef}
            style={{
              maxHeight: 360,
              overflowY: "auto",
              paddingRight: 6,
            }}
          >
            <List
              dataSource={chatMessages}
              renderItem={(m, idx) => (
                <List.Item key={idx} style={{ border: "none", padding: "8px 0" }}>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "82%",
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: m.role === "user" ? "#e6f4ff" : "#f5f5f5",
                        border: "1px solid rgba(0,0,0,0.06)",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <Text strong style={{ display: "block", marginBottom: 4 }}>
                        {m.role === "user" ? "You" : "Simpl'AI Coach"}
                      </Text>
                      <Text>{renderMarkdownLite(m.content)}</Text>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </Card>

      {isAdmin ? <DebugPanel debug={debugStep1} /> : null}

      <Card size="small" title="Your message">
        <Space direction="vertical" style={{ width: "100%" }}>
          <TextArea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            rows={4}
            placeholder="Example: I want an 8-week plan for FTP improvement, 4 rides/week, 45–60min weekdays + 90min weekend, smart trainer + HR, one rest day..."
            onPressEnter={(e) => {
              if (e.shiftKey) return;
              e.preventDefault();
              sendCoachMessage();
            }}
          />
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Button onClick={handleLeave} loading={savingDraft} disabled={savingDraft}>
              Leave
            </Button>
            <Space wrap>
              <Button type="primary" icon={<SendOutlined />} onClick={sendCoachMessage} loading={sending} disabled={!chatInput.trim()}>
                Send
              </Button>
              <Button
                type="primary"
                disabled={!isEnough || sending}
                loading={sending}
                onClick={() => {
                  if (sending) return;
                  setCurrentStep(1);
                }}
              >
                Let's build your program blueprint
              </Button>
            </Space>
          </Space>
        </Space>
      </Card>
    </Space>
  );

  const Step2 = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Program Blueprint"
        description="This is the high-level structure (week by week). Refine it until it matches what you want, then we’ll generate detailed sessions in Step 3."
      />

      <Card size="small" title="Program name/category (must be new)">
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Text type="secondary">Program name/category</Text>
          <Input
            value={programCategory}
            status={categoryError ? "error" : undefined}
            onChange={(e) => {
              didAutoSeedCategoryRef.current = true; // user has decided; never re-seed after this
              setProgramCategory(e.target.value);
            }}
            placeholder={blueprint?.plan_category || blueprint?.plan_name || "Choose a new category"}
          />
          {categoryChecking ? <Text type="secondary">Checking availability…</Text> : null}
          {categoryError ? <Text type="danger">{categoryError}</Text> : null}
          
        </Space>
      </Card>

      <Card size="small" title="Blueprint">
        {blueprintLoading ? (
          <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <Spin />
            <Text>Building your program blueprint…</Text>
          </div>
        ) : lastBlueprintError ? (
          <Alert type="error" showIcon message="Blueprint error" description={lastBlueprintError} />
        ) : !blueprint ? (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            No blueprint yet. Go back to Step 1 and make sure the coach has enough information.
          </Paragraph>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            <div>
              <Text strong>Program name/category:</Text> <Text>{blueprint.plan_category || blueprint.plan_name}</Text>
            </div>
            {!!blueprint.plan_description && (
              <div>
                <Text strong>Description:</Text>
                <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>{blueprint.plan_description}</Paragraph>
              </div>
            )}
            {!!blueprint.coach_philosophy && (
              <Alert
                type="success"
                showIcon
                message="Coach philosophy"
                description={<span style={{ whiteSpace: "pre-wrap" }}>{blueprint.coach_philosophy}</span>}
              />
            )}

            <Divider style={{ margin: "8px 0" }} />

            <Collapse accordion>
              {(blueprint.weeks || []).map((w) => (
                <Panel
                  header={`Week ${w.week_number}: ${w.title || ""}`}
                  key={String(w.week_number)}
                >
                  {!!w.purpose && (
                    <Paragraph style={{ whiteSpace: "pre-wrap" }}>
                      <Text strong>Purpose:</Text> {w.purpose}
                    </Paragraph>
                  )}
                  {(w.sessions || []).length === 0 ? (
                    <Paragraph type="secondary">No sessions listed for this week yet.</Paragraph>
                  ) : (
                    <List
                      size="small"
                      dataSource={w.sessions}
                      renderItem={(s, idx) => (
                        <List.Item key={`${w.week_number}-${idx}`}>
                          <div style={{ width: "100%" }}>
                            <Text strong>{s.name}</Text>
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary">
                                {typeof s.duration_min === "number" ? `${s.duration_min}` : "?"}
                                {typeof s.duration_max === "number" && s.duration_max !== s.duration_min ? `-${s.duration_max}` : ""}
                                {" min"} ·{" "}
                                {typeof s.tss_min === "number" ? `${s.tss_min}` : "?"}
                                {typeof s.tss_max === "number" && s.tss_max !== s.tss_min ? `-${s.tss_max}` : ""}
                                {" TSS"}
                              </Text>
                            </div>
                            {!!s.philosophy && (
                              <Paragraph style={{ marginTop: 6, marginBottom: 0, whiteSpace: "pre-wrap" }}>
                                {s.philosophy}
                              </Paragraph>
                            )}
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                </Panel>
              ))}
            </Collapse>
          </Space>
        )}
      </Card>

      {isAdmin ? <DebugPanel debug={debugStep2} /> : null}

      <Card size="small" title="Refine the blueprint">
        <Space direction="vertical" style={{ width: "100%" }}>
          <TextArea
            value={blueprintRefineText}
            onChange={(e) => setBlueprintRefineText(e.target.value)}
            rows={3}
            placeholder="Example: Make week 3 a deload; move the long ride to Sunday; reduce intensity; add one optional recovery ride…"
          />
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Button onClick={refineBlueprint} loading={blueprintLoading} disabled={!blueprintRefineText.trim()}>
              Update blueprint
            </Button>
            <Button
              type="primary"
              disabled={!blueprint || blueprintLoading || categoryChecking || !!categoryError || isOutOfCredits}
              onClick={() => setCurrentStep(2)}
            >
              Let’s create your individual sessions
            </Button>
          </Space>
        </Space>
      </Card>
    </Space>
  );

  const confirmBackToStep2 = () => {
    Modal.confirm({
      title: "Go back to Step 2?",
      content:
        "If you go back, all generated sessions will be lost. You can always regenerate individual sessions later if needed.",
      okText: "Go back (lose sessions)",
      cancelText: "Stay on Step 3",
      onOk: () => {
        setSessionWorkouts({});
        setSessionComments({});
        setSessionNameOverrides({});
        setStepDraftsBySession({});
        setSessionDebug({});
        setBatchFailures([]);
        setInitialBatchDone(false);
        autoBatchRanRef.current = false;
        setCurrentStep(1);
      },
    });
  };

  const Step3 = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Session Builder"
        description="Generate detailed step-by-step workouts for each session. Use the session note to regenerate, and you can edit title/description before saving."
      />

      {!blueprint ? (
        <Card size="small">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">No blueprint found. Go back to Step 2 and generate/accept a blueprint first.</Text>
            <Button onClick={confirmBackToStep2}>Back to Step 2</Button>
          </Space>
        </Card>
      ) : (
        <Card size="small" title="Sessions">
          {isOutOfCredits ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="You’ve used all your AI credits for this month"
              description={
                <Space wrap>
                  <span>Try again next month, or upgrade to Premium for unlimited credits.</span>
                  <RequestPremium
                    axiosInstance={axiosInstance}
                    buttonText="Upgrade Premium"
                    buttonProps={{ style: premiumBtnStyle }}
                    context="Simpl'AI Plan Builder - out of credits"
                  />
                </Space>
              }
            />
          ) : null}
          <Card size="small" style={{ marginBottom: 12 }} title="Program name/category (must be new)">
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <Text type="secondary">Program name/category</Text>
              <Input
                value={programCategory}
                status={categoryError ? "error" : undefined}
                onChange={(e) => {
                  didAutoSeedCategoryRef.current = true; // user has decided; never re-seed after this
                  setProgramCategory(e.target.value);
                }}
                placeholder="New category (must not already exist)"
              />
              {categoryChecking ? <Text type="secondary">Checking availability…</Text> : null}
              {categoryError ? <Text type="danger">{categoryError}</Text> : null}
              {!String(programCategory || "").trim() ? (
                <Text type="danger">
                  Category is required to save to your library (you can still generate sessions without it).
                </Text>
              ) : null}
            </Space>
          </Card>

          <Alert
            type="info"
            showIcon
            message="Power display: %FTP or Watts"
            description={
              <span>
                Workouts are always <b>saved as %FTP</b> with <b>ref FTP=100</b>. For convenience you can switch the editor + chart to show <b>Watts</b> using a display FTP.{" "}
                {typeof userFtp === "number" ? (
                  <span>
                    Your profile FTP is <b>{userFtp}W</b>.
                  </span>
                ) : null}
              </span>
            }
            style={{ marginBottom: 12 }}
          />

          <Card size="small" style={{ marginBottom: 12 }} title="Blueprint (reference)">
            {!blueprint ? (
              <Text type="secondary">No blueprint.</Text>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }} size={10}>
                <div>
                  <Text strong>Program:</Text> <Text>{blueprint.plan_category || blueprint.plan_name}</Text>
                </div>
                {!!blueprint.plan_description && (
                  <div>
                    <Text strong>Description:</Text>
                    <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>{blueprint.plan_description}</Paragraph>
                  </div>
                )}
                {!!blueprint.coach_philosophy && (
                  <Alert
                    type="success"
                    showIcon
                    message="Coach philosophy"
                    description={<span style={{ whiteSpace: "pre-wrap" }}>{blueprint.coach_philosophy}</span>}
                  />
                )}
                <Collapse>
                  <Panel header="Week-by-week outline" key="weeks">
                    <Collapse accordion>
                      {(blueprint.weeks || []).map((w) => (
                        <Panel header={`Week ${w.week_number}: ${w.title || ""}`} key={`bp-w-${w.week_number}`}>
                          {!!w.purpose && (
                            <Paragraph style={{ whiteSpace: "pre-wrap" }}>
                              <Text strong>Purpose:</Text> {w.purpose}
                            </Paragraph>
                          )}
                          {(w.sessions || []).length === 0 ? (
                            <Paragraph type="secondary">No sessions listed for this week yet.</Paragraph>
                          ) : (
                            <List
                              size="small"
                              dataSource={w.sessions}
                              renderItem={(s, idx) => (
                                <List.Item key={`bp-${w.week_number}-${idx}`}>
                                  <div style={{ width: "100%" }}>
                                    <Text strong>{s.name}</Text>
                                    <div style={{ marginTop: 4 }}>
                                      <Text type="secondary">
                                        {typeof s.duration_min === "number" ? `${s.duration_min}` : "?"}
                                        {typeof s.duration_max === "number" && s.duration_max !== s.duration_min ? `-${s.duration_max}` : ""}
                                        {" min"} ·{" "}
                                        {typeof s.tss_min === "number" ? `${s.tss_min}` : "?"}
                                        {typeof s.tss_max === "number" && s.tss_max !== s.tss_min ? `-${s.tss_max}` : ""}
                                        {" TSS"}
                                      </Text>
                                    </div>
                                    {!!s.philosophy && (
                                      <Paragraph style={{ marginTop: 6, marginBottom: 0, whiteSpace: "pre-wrap" }}>
                                        {s.philosophy}
                                      </Paragraph>
                                    )}
                                  </div>
                                </List.Item>
                              )}
                            />
                          )}
                        </Panel>
                      ))}
                    </Collapse>
                  </Panel>
                </Collapse>
              </Space>
            )}
          </Card>

          {/* While initial auto-batch is running, show only a loader (per requirement) */}
          {!initialBatchDone && (
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                <Spin />
                <Text>Generating all sessions…</Text>
              </div>
            </Card>
          )}

          {initialBatchDone &&
            (() => {
              const totalSessions = (blueprint.weeks || []).reduce((acc, w) => acc + ((w.sessions || []).length || 0), 0);
              const generatedCount = Object.values(sessionWorkouts || {}).filter(Boolean).length;
              const programMinutes = Math.round((effectiveProgramSeconds || 0) / 60);
              const plannedMinutes = Math.round((plannedProgramSeconds || 0) / 60);
              const builtMinutes = Math.round((builtProgramSeconds || 0) / 60);

              return (
                <Card size="small" style={{ marginBottom: 12 }} title="Status & actions">
                  <Space direction="vertical" style={{ width: "100%" }} size={10}>
                    <Space wrap>
                      <Tag color="blue">Total sessions: {totalSessions}</Tag>
                      <Tag color="purple">Program duration: {programMinutes} min</Tag>
                    </Space>
                    <Text type="secondary">
                      Built: {generatedCount}/{totalSessions} sessions · {builtMinutes} min built · {plannedMinutes} min planned
                    </Text>
                    <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
                      <Space wrap>
                        <Text type="secondary">Show power as</Text>
                        <Switch
                          checked={powerDisplayMode === "watts"}
                          disabled={!canShowWatts}
                          onChange={(checked) => setPowerDisplayMode(checked ? "watts" : "percent")}
                          checkedChildren="Watts"
                          unCheckedChildren="%FTP"
                        />
                        <Text type="secondary">Display FTP</Text>
                        <InputNumber
                          min={1}
                          step={1}
                          value={displayFtp}
                          style={{ width: 120 }}
                          placeholder={typeof userFtp === "number" ? String(userFtp) : "e.g. 250"}
                          onChange={(v) => {
                            const n = typeof v === "number" ? v : v == null ? null : Number(v);
                            setDisplayFtp(Number.isFinite(n) ? n : null);
                          }}
                        />
                        {!canShowWatts ? <Text type="secondary">(enter FTP to enable Watts)</Text> : null}
                      </Space>
                    </Space>
                  </Space>
                </Card>
              );
            })()}

          {/* During batch generation triggered by manual button */}
          {batchGenerating && initialBatchDone && (
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ padding: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <Spin />
                <Text>Generating all sessions…</Text>
              </div>
            </Card>
          )}

          {initialBatchDone && !batchGenerating && batchFailures.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Some sessions failed to generate"
              description={
                <div>
                  <Text type="secondary">You can open the session and click "Generate this workout".</Text>
                  <List
                    size="small"
                    dataSource={batchFailures}
                    renderItem={(r) => (
                      <List.Item>
                        <Space>
                          <Tag color="red">Failed</Tag>
                          <Text>{r.key}</Text>
                          <Text type="secondary">{r.error}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>
              }
            />
          )}

          {initialBatchDone && (
            <Collapse accordion>
            {(blueprint.weeks || []).map((w) => (
              <Panel header={`Week ${w.week_number}: ${w.title || ""}`} key={`w-${w.week_number}`}>
                {!!w.purpose && (
                  <Paragraph style={{ whiteSpace: "pre-wrap" }}>
                    <Text strong>Purpose:</Text> {w.purpose}
                  </Paragraph>
                )}

                <Collapse accordion>
                  {(w.sessions || []).map((s, idx) => {
                    const key = getSessionKey(w.week_number, idx);
                    const workout = sessionWorkouts[key];
                    const dbg = (sessionDebug || {})[key] || null;
                    const defaultTitle = composeSessionDisplayTitle(w.week_number, idx, workout?.title || s?.name || "");
                    const sessionTitle = String((sessionNameOverrides || {})[key] ?? defaultTitle);

                    const totalSec = computeWorkoutTotalSeconds(workout);
                    const actualMin = workout ? computeWorkoutDurationMinutes(workout) : 0;
                    const actualTss = workout ? computeWorkoutTss(workout) : null;

                    const columns = [
                      {
                        title: "#",
                        key: "idx",
                        width: 60,
                        render: (val, record, i) => i + 1,
                      },
                      {
                        title: "Title",
                        dataIndex: "title",
                        key: "title",
                        width: 180,
                        render: (value, record, i) => (
                          <Input
                            value={value || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSessionWorkouts((prev) => {
                                const next = { ...(prev || {}) };
                                const cur = next[key];
                                if (!cur) return prev;
                                const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                steps[i] = { ...(steps[i] || {}), title: v };
                                next[key] = { ...cur, steps };
                                return next;
                              });
                            }}
                          />
                        ),
                      },
                      {
                        title: "Duration (sec)",
                        dataIndex: "duration",
                        key: "duration",
                        width: 140,
                        render: (value, record, i) => {
                          const d = getDraft(key, i, "duration");
                          const displayValue = d !== undefined ? d : value ?? "";
                          const err = getFieldError(key, i, "duration", workout);
                          return (
                            <div>
                              <Input
                                status={err ? "error" : undefined}
                                value={displayValue}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setDraft(key, i, "duration", raw);
                                  const parsed = parseIntStrict(raw);
                                  const nextErr = validateDuration(parsed.ok ? parsed.value : NaN);
                                  if (!nextErr) {
                                    setSessionWorkouts((prev) => {
                                      const next = { ...(prev || {}) };
                                      const cur = next[key];
                                      if (!cur) return prev;
                                      const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                      steps[i] = { ...(steps[i] || {}), duration: parsed.value };
                                      next[key] = { ...cur, steps };
                                      return next;
                                    });
                                    clearDraft(key, i, "duration");
                                  }
                                }}
                              />
                              {err ? (
                                <Text type="danger" style={{ fontSize: 12 }}>
                                  {err}
                                </Text>
                              ) : null}
                            </div>
                          );
                        },
                      },
                      {
                        title: powerDisplayMode === "watts" ? "Power (W)" : "Power (%FTP)",
                        dataIndex: "watt",
                        key: "watt",
                        width: 130,
                        render: (value, record, i) => {
                          const d = getDraft(key, i, "watt");
                          const basePct = typeof value === "number" ? value : Number(value);
                          const computedWatts = powerPercentToWatts(Number.isFinite(basePct) ? basePct : NaN);
                          const displayValue = d !== undefined ? d : powerDisplayMode === "watts" ? (computedWatts || "") : (value ?? "");
                          const err = getFieldError(key, i, "watt", workout);
                          return (
                            <div>
                              <Input
                                status={err ? "error" : undefined}
                                value={displayValue}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setDraft(key, i, "watt", raw);
                                  const parsed = parseIntStrict(raw);
                                  if (!parsed.ok) return;

                                  if (powerDisplayMode === "watts") {
                                    if (!canShowWatts) return;
                                    const wErr = validateWatts(parsed.value);
                                    if (wErr) return;
                                    const pct = powerWattsToPercent(parsed.value);
                                    const pctErr = validatePercent(pct);
                                    if (pctErr) return;
                                    setSessionWorkouts((prev) => {
                                      const next = { ...(prev || {}) };
                                      const cur = next[key];
                                      if (!cur) return prev;
                                      const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                      steps[i] = { ...(steps[i] || {}), watt: pct };
                                      next[key] = { ...cur, steps };
                                      return next;
                                    });
                                    clearDraft(key, i, "watt");
                                    return;
                                  }

                                  const pctErr = validatePercent(parsed.value);
                                  if (pctErr) return;
                                  if (canShowWatts) {
                                    const w = powerPercentToWatts(parsed.value);
                                    const wErr2 = validateWatts(Number(w));
                                    if (wErr2) return;
                                  }
                                  setSessionWorkouts((prev) => {
                                    const next = { ...(prev || {}) };
                                    const cur = next[key];
                                    if (!cur) return prev;
                                    const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                    steps[i] = { ...(steps[i] || {}), watt: parsed.value };
                                    next[key] = { ...cur, steps };
                                    return next;
                                  });
                                  clearDraft(key, i, "watt");
                                }}
                              />
                              {err ? (
                                <Text type="danger" style={{ fontSize: 12 }}>
                                  {err}
                                </Text>
                              ) : null}
                            </div>
                          );
                        },
                      },
                      {
                        title: "RPM",
                        dataIndex: "rpm",
                        key: "rpm",
                        width: 90,
                        render: (value, record, i) => {
                          const d = getDraft(key, i, "rpm");
                          const displayValue = d !== undefined ? d : (Number(value) > 0 ? value : "");
                          const err = getFieldError(key, i, "rpm", workout);
                          return (
                            <div>
                              <Input
                                status={err ? "error" : undefined}
                                value={displayValue}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setDraft(key, i, "rpm", raw);
                                  const trimmed = String(raw || "").trim();
                                  // empty/0 => "none"
                                  if (!trimmed || trimmed === "0") {
                                    setSessionWorkouts((prev) => {
                                      const next = { ...(prev || {}) };
                                      const cur = next[key];
                                      if (!cur) return prev;
                                      const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                      steps[i] = { ...(steps[i] || {}), rpm: 0 };
                                      next[key] = { ...cur, steps };
                                      return next;
                                    });
                                    clearDraft(key, i, "rpm");
                                    return;
                                  }
                                  const parsed = parseIntStrict(raw);
                                  const nextErr = validateRpm(parsed.ok ? parsed.value : NaN);
                                  if (!nextErr) {
                                    setSessionWorkouts((prev) => {
                                      const next = { ...(prev || {}) };
                                      const cur = next[key];
                                      if (!cur) return prev;
                                      const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                      steps[i] = { ...(steps[i] || {}), rpm: parsed.value };
                                      next[key] = { ...cur, steps };
                                      return next;
                                    });
                                    clearDraft(key, i, "rpm");
                                  }
                                }}
                              />
                              {err ? (
                                <Text type="danger" style={{ fontSize: 12 }}>
                                  {err}
                                </Text>
                              ) : null}
                            </div>
                          );
                        },
                      },
                      {
                        title: "Comment",
                        dataIndex: "comment",
                        key: "comment",
                        render: (value, record, i) => (
                          <Input
                            value={value || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSessionWorkouts((prev) => {
                                const next = { ...(prev || {}) };
                                const cur = next[key];
                                if (!cur) return prev;
                                const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                steps[i] = { ...(steps[i] || {}), comment: v };
                                next[key] = { ...cur, steps };
                                return next;
                              });
                            }}
                          />
                        ),
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        width: 70,
                        render: (value, record, i) => (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            title="Remove step"
                            aria-label="Remove step"
                            onClick={() => {
                              setSessionWorkouts((prev) => {
                                const next = { ...(prev || {}) };
                                const cur = next[key];
                                if (!cur) return prev;
                                const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                if (steps.length <= STEP_RULES.minSteps) return prev;
                                steps.splice(i, 1);
                                next[key] = { ...cur, steps };
                                return next;
                              });
                              clearSessionDrafts(key);
                            }}
                            disabled={!workout || (Array.isArray(workout?.steps) ? workout.steps.length : 0) <= STEP_RULES.minSteps}
                          />
                        ),
                      },
                    ];

                    const header = (
                      <Space wrap>
                        <Text strong>{sessionTitle}</Text>
                        {workout ? (
                          <Text type="secondary">
                            {actualMin || 0} min · {actualTss != null ? Math.round(actualTss) : "?"} TSS
                          </Text>
                        ) : (
                          <Text type="secondary">
                            {typeof s.duration_min === "number" ? `${s.duration_min}` : "?"}
                            {typeof s.duration_max === "number" && s.duration_max !== s.duration_min ? `-${s.duration_max}` : ""}
                            {" min"} ·{" "}
                            {typeof s.tss_min === "number" ? `${s.tss_min}` : "?"}
                            {typeof s.tss_max === "number" && s.tss_max !== s.tss_min ? `-${s.tss_max}` : ""}
                            {" TSS"}
                          </Text>
                        )}
                        {workout ? <Tag color="blue">{Math.round(totalSec / 60)} min built</Tag> : null}
                      </Space>
                    );

                    return (
                      <Panel header={header} key={`s-${w.week_number}-${idx}`}>
                        {!!s.philosophy && (
                          <Paragraph style={{ marginTop: 0, whiteSpace: "pre-wrap" }}>{s.philosophy}</Paragraph>
                        )}

                        <Space direction="vertical" style={{ width: "100%" }} size={10}>
                          <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                            <Space wrap>
                              <Button
                                type="primary"
                                loading={sessionBuilderLoading}
                                disabled={categoryChecking || !!categoryError || batchGenerating || isOutOfCredits}
                                onClick={async () => {
                                  if (isOutOfCredits) {
                                    openPremiumModal("Simpl'AI Plan Builder - Step 3 (Session Builder) - out of credits");
                                    return;
                                  }
                                  const note = String((sessionComments || {})[key] || "").trim();
                                  setSessionBuilderLoading(true);
                                  try {
                                    const resp = await axiosInstance.post("/ai/plan_builder/step3/", {
                                      blueprint,
                                      week_number: w.week_number,
                                      session_index: idx,
                                      comment: note || undefined,
                                      previous_workout: workout || undefined,
                                      // Allow generation even when category is empty; backend will fall back to blueprint category.
                                      program_category: programCategory.trim() || undefined,
                                      // Carry-forward: ensure this session sees the Step 1 reformulation (global requirements/constraints).
                                      reformulated_desiderata: lastReformulation || undefined,
                                    });
                                    const wkt = resp?.data?.workout;
                                    try {
                                      if (typeof resp?.data?.credits_remaining === "number") setCreditsRemaining(resp.data.credits_remaining);
                                      if (typeof resp?.data?.credits_limit === "number") setCreditsLimit(resp.data.credits_limit);
                                      if (typeof resp?.data?.is_premium === "boolean") setIsPremium(resp.data.is_premium);
                                    } catch {}
                                    const dbgResp = resp?.data?.debug ? capDebug(resp.data.debug) : null;
                                    const adminForThis = isAdmin || !!dbgResp;
                                    if (dbgResp) setIsAdmin(true);
                                    if (!wkt) throw new Error("Missing workout in response");
                                    const fixed = {
                                      ...wkt,
                                      category: programCategory.trim() || wkt.category,
                                    };
                                    const computedTitle = composeSessionDisplayTitle(w.week_number, idx, fixed.title || s?.name || "");
                                    fixed.title = computedTitle;
                                    if (dbgResp) {
                                      setSessionDebug((prev) => ({ ...(prev || {}), [key]: dbgResp }));
                                    }
                                    // This is AI generation (not a manual edit) → don't nag on Leave.
                                    requestBaselineReset();
                                    // Clear any invalid draft edits for this session (new workout overwrote steps)
                                    clearSessionDrafts(key);
                                    setSessionWorkouts((prev) => {
                                      const next = { ...(prev || {}), [key]: fixed };
                                      // Persist immediately so single-session generation isn't lost.
                                      emitPersistState({
                                        status: "in_progress",
                                        current_step: 2,
                                        blueprint,
                                        program_category: programCategory.trim(),
                                        session_workouts: next,
                                        debug: adminForThis
                                          ? {
                                              step1: debugStep1,
                                              step2: debugStep2,
                                              sessions: { ...(sessionDebug || {}), ...(dbgResp ? { [key]: dbgResp } : {}) },
                                            }
                                          : undefined,
                                      });
                                      return next;
                                    });
                                    setSessionNameOverrides((prev) => {
                                      // Only auto-fill if the user hasn't customized.
                                      if (prev && Object.prototype.hasOwnProperty.call(prev, key)) return prev;
                                      const next = { ...(prev || {}), [key]: computedTitle };
                                      emitPersistState({
                                        status: "in_progress",
                                        current_step: 2,
                                        blueprint,
                                        program_category: programCategory.trim(),
                                        session_name_overrides: next,
                                      });
                                      return next;
                                    });
                                  } catch (e) {
                                    console.error("Simpl'AI plan builder step3 error:", e);
                                    if (!handleAiError(e)) message.error("Failed to build workout. Please try again.");
                                  } finally {
                                    setSessionBuilderLoading(false);
                                  }
                                }}
                              >
                                {workout ? "Regenerate this workout (overwrite)" : "Generate this workout"}
                              </Button>
                            </Space>

                            <Button onClick={confirmBackToStep2}>Adjust blueprint</Button>
                          </Space>

                          <Space direction="vertical" style={{ width: "100%" }} size={6}>
                            <Text strong>Workout title (session name)</Text>
                            <Input
                              value={sessionTitle}
                              onChange={(e) => {
                                const v = e.target.value;
                                // Keep workout.title and persisted draft state in sync (single concept).
                                setSessionNameOverrides((prevOverrides) => {
                                  const nextOverrides = { ...(prevOverrides || {}), [key]: v };
                                  setSessionWorkouts((prevWorkouts) => {
                                    const nextWorkouts = { ...(prevWorkouts || {}) };
                                    const cur = nextWorkouts[key];
                                    if (!cur) return prevWorkouts;
                                    nextWorkouts[key] = { ...cur, title: v };
                                    emitPersistState({
                                      status: "in_progress",
                                      current_step: 2,
                                      blueprint,
                                      program_category: programCategory.trim(),
                                      session_workouts: nextWorkouts,
                                      session_name_overrides: nextOverrides,
                                    });
                                    return nextWorkouts;
                                  });
                                  return nextOverrides;
                                });
                              }}
                              placeholder={defaultTitle}
                            />
                            <Text strong>Regenerate note (optional)</Text>
                            <TextArea
                              value={(sessionComments || {})[key] || ""}
                              onChange={(e) => setSessionComments((prev) => ({ ...(prev || {}), [key]: e.target.value }))}
                              rows={2}
                              placeholder="Example: make it easier; add cadence drills; longer warmup; swap VO2 for tempo…"
                            />
                          </Space>
                        </Space>

                        {isAdmin ? <DebugPanel debug={dbg} /> : null}

                        {!workout ? (
                          <Paragraph type="secondary" style={{ marginTop: 10 }}>
                            This session hasn’t built yet (or failed). Add a note above if you want, then click "Generate this workout".
                          </Paragraph>
                        ) : (
                          <>
                            <Divider style={{ margin: "10px 0" }} />
                            <div style={{ marginBottom: 10 }}>
                              <Text strong>Category (program name)</Text> <Text>{programCategory.trim() || "(not set yet)"}</Text>
                              <div style={{ marginTop: 8 }}>
                                <Text strong>Workout description</Text>
                                <TextArea
                                  value={workout.description || ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setSessionWorkouts((prev) => ({ ...(prev || {}), [key]: { ...(prev?.[key] || workout), description: v } }));
                                  }}
                                  rows={3}
                                  placeholder="(Optional) Add your own notes"
                                />
                              </div>
                            </div>

                            <Space wrap style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
                              <Space wrap>
                                <Button
                                  onClick={() => {
                                    setSessionWorkouts((prev) => {
                                      const next = { ...(prev || {}) };
                                      const cur = next[key];
                                      if (!cur) return prev;
                                      const steps = Array.isArray(cur.steps) ? [...cur.steps] : [];
                                      if (steps.length >= STEP_RULES.maxSteps) return prev;
                                      steps.push({
                                        title: "Interval",
                                        duration: STEP_RULES.durationMinSec,
                                        watt: STEP_RULES.pctMin,
                                        rpm: 0,
                                        comment: "",
                                      });
                                      next[key] = { ...cur, steps };
                                      return next;
                                    });
                                    clearSessionDrafts(key);
                                  }}
                                  disabled={!workout || (Array.isArray(workout?.steps) ? workout.steps.length : 0) >= STEP_RULES.maxSteps}
                                >
                                  Add step
                                </Button>
                                <Text type="secondary">
                                  Steps: {(Array.isArray(workout?.steps) ? workout.steps.length : 0)}/{STEP_RULES.maxSteps}
                                </Text>
                              </Space>
                              {(() => {
                                try {
                                  const steps = Array.isArray(workout?.steps) ? workout.steps : [];
                                  if (steps.length < STEP_RULES.minSteps || steps.length > STEP_RULES.maxSteps) return <Text type="danger">Invalid step count.</Text>;
                                  for (let si = 0; si < steps.length; si++) {
                                    if (getFieldError(key, si, "duration", workout)) return <Text type="danger">Invalid values (see red fields).</Text>;
                                    if (getFieldError(key, si, "watt", workout)) return <Text type="danger">Invalid values (see red fields).</Text>;
                                    if (getFieldError(key, si, "rpm", workout)) return <Text type="danger">Invalid values (see red fields).</Text>;
                                  }
                                  return <Text type="secondary">All values look valid.</Text>;
                                } catch {
                                  return <Text type="danger">Invalid values (see red fields).</Text>;
                                }
                              })()}
                            </Space>

                            <Table
                              columns={columns}
                              dataSource={Array.isArray(workout.steps) ? workout.steps : []}
                              rowKey={(r, i) => `${key}-step-${i}`}
                              pagination={false}
                              size="small"
                              scroll={{ x: 900 }}
                            />

                            <Divider style={{ margin: "10px 0" }} />
                           

                            <div style={{ marginTop: 12 }}>
                              <Text type="secondary">
                                {powerDisplayMode === "watts" && canShowWatts ? (
                                  <>
                                    Chart below uses <Text strong>FTP={displayFtp}</Text>, so the Y-axis is in <Text strong>Watts</Text>.
                                  </>
                                ) : (
                                  <>
                                    Chart below uses <Text strong>FTP=100</Text>, so the Y-axis is effectively <Text strong>%FTP</Text>.
                                  </>
                                )}
                              </Text>
                              <TrainingChart
                                steps={
                                  powerDisplayMode === "watts" && canShowWatts
                                    ? (workout.steps || []).map((st) => ({
                                        ...(st || {}),
                                        // TrainingChart expects absolute Watts in step.watt
                                        watt: Math.round(((Number(st?.watt) || 0) * displayFtp) / 100),
                                      }))
                                    : workout.steps || []
                                }
                                FTP={powerDisplayMode === "watts" && canShowWatts ? displayFtp : 100}
                                isMobile={false}
                              />
                            </div>
                          </>
                        )}
                      </Panel>
                    );
                  })}
                </Collapse>
              </Panel>
            ))}
            </Collapse>
          )}

          <Divider style={{ margin: "10px 0" }} />
          {(() => {
            const totalSessions = (blueprint.weeks || []).reduce((acc, w) => acc + ((w.sessions || []).length || 0), 0);
            const generatedCount = Object.values(sessionWorkouts || {}).filter(Boolean).length;
            const done = totalSessions > 0 && generatedCount >= totalSessions;
            return (
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Button onClick={confirmBackToStep2}>Back to Step 2</Button>
                <Space wrap>
                  <Button onClick={resumeEditingLater} loading={savingDraft} disabled={savingDraft}>
                    Resume editing later
                  </Button>
                  <Button
                    type="primary"
                    disabled={!done || categoryChecking || !!categoryError || programSaving}
                    loading={programSaving}
                    onClick={async () => {
                    if (!done) return;
                    setProgramSaving(true);
                    try {
                      if (categoryChecking) {
                        message.error("Please wait for category availability check to finish.");
                        return;
                      }
                      if (!String(programCategory || "").trim()) {
                        Modal.warning({
                          title: "Missing program name/category",
                          content: "You must enter a category name before saving to the library.",
                          okText: "Got it",
                        });
                        return;
                      }
                      // Validate: session name must not be empty (block save if user cleared a title)
                      try {
                        const weeks = Array.isArray(blueprint?.weeks) ? blueprint.weeks : [];
                        for (const w of weeks) {
                          for (let i = 0; i < ((w.sessions || []).length || 0); i++) {
                            const k = getSessionKey(w.week_number, i);
                            const s = (w.sessions || [])[i];
                            const workoutTitle = sessionWorkouts?.[k]?.title || s?.name || "";
                            const defaultName = composeSessionDisplayTitle(w.week_number, i, workoutTitle);
                            const hasOverride = !!(sessionNameOverrides && Object.prototype.hasOwnProperty.call(sessionNameOverrides, k));
                            const rawName = hasOverride ? sessionNameOverrides[k] : defaultName;
                            if (!String(rawName || "").trim()) {
                              Modal.warning({
                                title: "Missing session name",
                                content: "Session name shouldn’t be empty. Please enter a name before saving.",
                                okText: "Got it",
                              });
                              return;
                            }
                          }
                        }
                      } catch {
                        Modal.warning({
                          title: "Missing session name",
                          content: "Session name shouldn’t be empty. Please enter a name before saving.",
                          okText: "Got it",
                        });
                        return;
                      }
                      if (categoryError) {
                        message.error("Fix category before saving.");
                        return;
                      }
                      // Validate all workout steps before saving (red borders + inline comments; block save with a modal)
                      try {
                        const weeks = Array.isArray(blueprint?.weeks) ? blueprint.weeks : [];
                        for (const w of weeks) {
                          for (let i = 0; i < ((w.sessions || []).length || 0); i++) {
                            const k = getSessionKey(w.week_number, i);
                            const wk = sessionWorkouts?.[k];
                            if (!wk) continue;
                            const steps = Array.isArray(wk?.steps) ? wk.steps : [];
                            if (steps.length < STEP_RULES.minSteps || steps.length > STEP_RULES.maxSteps) {
                              Modal.error({
                                title: "Fix invalid values",
                                content: (
                                  <span style={{ whiteSpace: "pre-wrap" }}>
                                    {"Some values are incorrect and should be fixed.\n\nSee the red fields and their comments."}
                                  </span>
                                ),
                                okText: "Got it",
                              });
                              return;
                            }
                            for (let si = 0; si < steps.length; si++) {
                              if (getFieldError(k, si, "duration", wk) || getFieldError(k, si, "watt", wk) || getFieldError(k, si, "rpm", wk)) {
                                Modal.error({
                                  title: "Fix invalid values",
                                  content: (
                                    <span style={{ whiteSpace: "pre-wrap" }}>
                                      {"Some values are incorrect and should be fixed.\n\nSee the red fields and their comments."}
                                    </span>
                                  ),
                                  okText: "Got it",
                                });
                                return;
                              }
                            }
                          }
                        }
                      } catch {
                        Modal.error({
                          title: "Fix invalid values",
                          content: (
                            <span style={{ whiteSpace: "pre-wrap" }}>
                              {"Some values are incorrect and should be fixed.\n\nSee the red fields and their comments."}
                            </span>
                          ),
                          okText: "Got it",
                        });
                        return;
                      }
                      const results = [];
                      let previousUuid = null;
                      // Capture uuid of the *first session* (Week min, Session 1) so we can auto-load it after refetch.
                      let firstSessionUuid = null;
                      let firstSessionKey = null;
                      try {
                        const weeks = Array.isArray(blueprint?.weeks) ? blueprint.weeks : [];
                        const weekNums = weeks
                          .filter((w) => w && typeof w.week_number === "number" && (w.sessions || []).length > 0)
                          .map((w) => w.week_number);
                        const minWeek = weekNums.length ? Math.min(...weekNums) : null;
                        if (typeof minWeek === "number" && Number.isFinite(minWeek)) {
                          firstSessionKey = getSessionKey(minWeek, 0);
                        }
                      } catch {
                        firstSessionKey = null;
                      }
                      for (const w of blueprint.weeks || []) {
                        for (let i = 0; i < ((w.sessions || []).length || 0); i++) {
                          const s = (w.sessions || [])[i];
                          const k = getSessionKey(w.week_number, i);
                          const workout = sessionWorkouts[k];
                          if (!workout) {
                            results.push({ key: k, ok: false, error: "Missing workout data" });
                            continue;
                          }

                          const sessionNameRaw = (sessionNameOverrides || {})[k];
                          const workoutTitle = sessionWorkouts?.[k]?.title || s?.name || "";
                          const defaultName = composeSessionDisplayTitle(w.week_number, i, workoutTitle);
                          const sessionName = String(sessionNameRaw ?? defaultName).trim() || defaultName;
                          const planName = sessionName; // saved as-is
                          const category = programCategory.trim(); // no /week suffix
                          const descriptionParts = [];
                          if (blueprint.plan_description) descriptionParts.push(blueprint.plan_description);
                          if (blueprint.coach_philosophy) descriptionParts.push(`Coach philosophy:\n${blueprint.coach_philosophy}`);
                          if (w.purpose) descriptionParts.push(`Week purpose: ${w.purpose}`);
                          if (s?.philosophy) descriptionParts.push(`Session philosophy: ${s.philosophy}`);
                          if (workout.description) descriptionParts.push(`Workout notes: ${workout.description}`);

                          const payload = {
                            name: planName,
                            category,
                            description: descriptionParts.filter(Boolean).join("\n\n"),
                            category_description: blueprint.plan_description || undefined,
                            category_philosophy: blueprint.coach_philosophy || undefined,
                            ref_ftp: 100,
                            stress_points: workout.stress_points || undefined,
                            previous_uuid: previousUuid || null,
                            steps: (workout.steps || []).map((st) => ({
                              title: st.title,
                              comment: st.comment,
                              duration: st.duration,
                              watt: st.watt, // %FTP
                              rpm: st.rpm,
                            })),
                          };

                          try {
                            const resp = await axiosInstance.post("/save_training_plan/", payload);
                            const newUuid = resp?.data?.uuid;
                            results.push({ key: k, ok: true, uuid: newUuid });
                            if (newUuid) previousUuid = newUuid;
                            if (!firstSessionUuid && newUuid && firstSessionKey && k === firstSessionKey) {
                              firstSessionUuid = newUuid;
                            }
                          } catch (e) {
                            const msg = e?.response?.data?.detail || e?.message || "Failed to save";
                            results.push({ key: k, ok: false, error: msg });
                          }
                        }
                      }
                      const okCount = results.filter((r) => r.ok).length;
                      const failCount = results.filter((r) => !r.ok).length;
                      if (failCount === 0) {
                        const lastSavedUuid = previousUuid;
                        // Fallback: if we couldn't identify the first session key for some reason,
                        // use the first successful save as the "auto-load" target.
                        const fallbackFirst = (results || []).find((r) => r && r.ok && r.uuid)?.uuid || null;
                        const autoLoadUuid = firstSessionUuid || fallbackFirst || lastSavedUuid || null;
                        // Best-effort: clear cached sessions now that the program is saved.
                        try {
                          const cacheId = String(programCategory || "").trim();
                          if (cacheId) await axiosInstance.post("/ai/plan_builder/session_cache/clear/", { cache_id: cacheId });
                        } catch (e) {
                          // ignore
                        }
                        // Mark the builder as completed on backend (clears Firestore draft).
                        try {
                          await axiosInstance.post("/ai/plan_builder/complete/", {});
                        } catch (e) {
                          // ignore
                        }
                        // Clear draft state locally so next open starts fresh.
                        try {
                          if (typeof onPersistState === "function") onPersistState(null);
                        } catch (e) {
                          // ignore
                        }

                        // Close the modal.
                        try {
                          if (typeof onClose === "function") onClose();
                        } catch (e) {
                          // ignore
                        }

                        // Refetch default plans (same intent as PlanSetup "Reset")
                        try {
                          // Passing uuid triggers App.js refetch + auto-select (auto-load) after plans are refreshed.
                          if (typeof onAfterFinalSave === "function") onAfterFinalSave({ uuid: autoLoadUuid, first_uuid: autoLoadUuid, last_uuid: lastSavedUuid });
                        } catch (e) {
                          // ignore
                        }

                        // Success popup (single "Got it").
                        try {
                          const cat = String(programCategory || "").trim() || "your new category";
                          Modal.success({
                            title: "Saved",
                            content: (
                              <span style={{ whiteSpace: "pre-wrap" }}>
                                {`Saved successfully. You can find your plans under the "${cat}" category in your library.\n\nIn a few seconds, your first session will be automatically loaded.`}
                              </span>
                            ),
                            okText: "Got it",
                          });
                        } catch (e) {
                          // ignore
                        }
                      }
                      else message.warning(`Saved ${okCount} sessions; ${failCount} failed.`);
                    } finally {
                      setProgramSaving(false);
                    }
                  }}
                  >
                    Save to library
                  </Button>
                </Space>
              </Space>
            );
          })()}
        </Card>
      )}
    </Space>
  );

  return (
    <LiquidGlass className="glass-section" style={{ marginTop: 12, marginBottom: 32 }}>
      <RequestPremium axiosInstance={axiosInstance} hideButton ref={premiumModalRef} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <Space wrap align="center">
          <Text strong>Simpl'AI Plan Builder</Text>
          {!userInfoLoaded ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Loading account…
            </Text>
          ) : isPremium ? (
            <Tag color="gold">
              <Space size={6}>
                <StarFilled />
                <span>{creditsLabel}</span>
              </Space>
            </Tag>
          ) : (
            <Tag color={isOutOfCredits ? "red" : "blue"}>{creditsLabel}</Tag>
          )}
        </Space>
        <Space wrap>
          {userInfoLoaded && !isPremium ? (
            <RequestPremium
              axiosInstance={axiosInstance}
              buttonText="Upgrade to Premium (Unlimited)"
              buttonProps={{ style: premiumBtnStyle }}
              context="Simpl'AI Plan Builder"
            />
          ) : null}
          <Button danger onClick={handleLeave} loading={savingDraft} disabled={savingDraft}>
            Leave
          </Button>
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 12}}>
        <Steps current={currentStep} items={stepItems} />
      </Card>

      {currentStep === 0 && Step1}
      {currentStep === 1 && Step2}
      {currentStep === 2 && Step3}
    </LiquidGlass>
  );
}

export default AiPlansBuilder;


